/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import Game from '../Game/Game';
import GameSetup from '../Game/GameSetup';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import ObservableIntComponent from '../Utility/Component/Observable/ObservableIntComponent';
import StateComponent from '../StateManager/StateComponent';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Manages the auto play functionality of the game.
 */
@cc._decorator.ccclass
export class AutoPlayManager extends StateDependencyComponent
{
    /** State on which to check for pending auto spins. */
    @property({ type: [StateReference], visible: true})
    protected autoSpinState: StateReference[] =
    [
        new StateReference(StateID.WaitForPlay),
        new StateReference(StateID.PostPlay)
    ];

    /** Delay to use between autoplay spins, in seconds. */
    @property({ type: cc.Float, visible: true, tooltip: "In Seconds" })
    private _autoPlayDelay: number = 1.0;

    /**
     * Injected reference to the auto plays remaining observable.
     * @hidden
     */

    private _autoPlayObservableRef: InjectableComponent<ObservableIntComponent>
        = new InjectableComponent<ObservableIntComponent>(ReferenceGroup.MainGame, "AutoPlay.Remaining");
    /**
     * Accessor for the observable count of remaining autoplays.
     */
    public get AutoPlayRemainingObservable(): ObservableIntComponent { return this._autoPlayObservableRef.Value; }

    /** @hidden */
    private _gameRef: InjectableComponent<Game> = new InjectableComponent<Game>();

    /** @hidden */
    protected gameSetupRef: InjectableComponent<GameSetup> = new InjectableComponent<GameSetup>();

    /** @hidden */
    private _pendingAutoPlay: boolean = false;

    /** @hidden */
    protected setContexts(): void
    {
        this._autoPlayObservableRef.SetContext(ObservableIntComponent);
        this._gameRef.SetContext(Game);
        this.gameSetupRef.SetContext(GameSetup);
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this.autoSpinState);

        this.configureAutoPlayObservable();

        this._gameRef.Value.AdvancePlayEventDispatcher
            .on(Game.AdvancePlayEventName, () => this.advancePlayEventHandler());
    }

    private configureAutoPlayObservable(): void
    {
        if (!this.AutoPlayRemainingObservable) { return; }

        this.AutoPlayRemainingObservable.OnChanged(this.autoPlayChangedHandler.bind(this));

        if (!this.gameSetupRef.Value) { return; }

        this.gameSetupRef.Value.OnSetupReceived(() =>
        {
            let options: number[] = this.gameSetupRef.Value.AutoSpinOptions;

            if (!options) { return; }

            this.AutoPlayRemainingObservable.MaxValue = this.gameSetupRef.Value.AutoSpinLimit;
            this.AutoPlayRemainingObservable.MinValue = 0;
        });
    }

    /** @hiddeonStateChangeHandler */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        if (this.autoSpinState.find(state => state.Value === event.State))
        {
            this.checkForAutoSpin(event.State);
        }
    }

    /** Updates internal autoplay state  */
    private autoPlayChangedHandler = (event: cc.Event.EventCustom): void =>
    {
        if (event.detail - this.AutoPlayRemainingObservable.OldValue !== -1)
        {
            this._pendingAutoPlay = true;
        }
    }

    /** @hidden */
    private advancePlayEventHandler = (): void =>
    {
        if (this._pendingAutoPlay && this.autoSpinState.find(StateReference.IsCurrentFilter))
        {
            this._pendingAutoPlay = false;
            this.DecreaseAutoPlay();
            return;
        }
    }

    /** Determines if an auto spin should be scheduled. */
    private checkForAutoSpin(state: StateComponent): void
    {
        if (this.IsAutoPlaying)
        {
            this.scheduleOnce(this.executeAutoPlay, this._autoPlayDelay);
        }

        this.completeState(state);
    }

    /** If still autoplaying, executes a auto spin. */
    private executeAutoPlay = (): void =>
    {
        if (this.IsAutoPlaying)
        {
            this.DecreaseAutoPlay();
            this._gameRef.Value.AdvancePlay();
        }
    }

    /** Accessor for remaining scheduled auto plays. */
    public get AutoPlays(): number
    {
        return this.AutoPlayRemainingObservable.Value;
    }

    /** Mutator for remaining scheduled auto plays. */
    public set AutoPlays(value: number)
    {
        this.AutoPlayRemainingObservable.Value = value;
    }

    /**
     * Convenience accessor for checking if remaining auto plays is greater than 0.
     * @returns If autoplays remaining > 0.
     */
    public get IsAutoPlaying(): boolean
    {
        return this.AutoPlayRemainingObservable.Value > 0 && !this._pendingAutoPlay;
    }

    /** Clears any schedule autoplays and sets remaining count to 0. */
    public ClearAutoPlay(): void
    {
        this.AutoPlayRemainingObservable.Value = 0;
    }

    /** Pauses the AutoPlay system without clearing the counter */
    public PauseAutoPlay(): void
    {
        this._pendingAutoPlay = true;
    }

    /**
     * Convenience method that decrements remaining autoplays by 1.
     * @returns The reamining autoplay count.
     */
    public DecreaseAutoPlay(): number
    {
        if (this.AutoPlayRemainingObservable.Value < 1)
        {
            return (this.AutoPlayRemainingObservable.Value = 0);
        }

        return --this.AutoPlayRemainingObservable.Value;
    }

    /**
     * Register a function to be called when the remaining autoplay count changes.
     * @param fnCallBack The function to be called.
     */
    public OnAutoPlayRemainingChanged(fnCallBack: () => void): void
    {
        this.AutoPlayRemainingObservable.OnChanged(fnCallBack);
    }
}
